/**
 * Validation functions to pass to redux-form fields.
 * The function returns an appropriate error message if validation failed, otherwise returns undefined.
 * The error message is automatically shown by the redux-form field.
 */

/* eslint-disable no-confusing-arrow */

export const minLength = min => value =>
	!value || value.length < min ? `Must be ${min} characters or more` : undefined;
export const minLength1 = minLength(1);
export const minLength4 = minLength(4);
export const minLength8 = minLength(8);
export const maxLength = max => value =>
        !value || value.length > max ? `Must be ${max} characters or less` : undefined;
export const maxLengthWithEmpty = max => value =>
        (value && value.length > max) ? `Must be ${max} characters or less` : undefined;
export const maxLength40 = maxLength(40);
export const maxLength50 = maxLength(50);
export const equalLengthDigits = num => value =>
	!value || value.length !== num ? `Must contain ${num} digits` : undefined;
export const requiredText = text => value =>
        !value || /^\s+$/.test(value) ? `${text} is required` : undefined;
export const requiredText1 = text => value =>
        !value || /^\s+$/.test(value) ? `${text}` : undefined;
export const name = value => !value || !/^[a-z .'-]+$/i.test(value) ? 'No special characters allowed' : undefined;
export const numeric = value => !value || !/^[0-9]+$/i.test(value) ? 'Must be numeric' : undefined;
export const alphaNumeric = value => !value || !/^[a-z0-9]+$/i.test(value) ? 'Must be alphanumeric' : undefined;
export const alphaNumericWithSpace = value => !value || !/^[a-z0-9/\s]+$/i.test(value) ? 'Must be alphanumeric' : undefined;
export const alphaNumericAsterisk = value => !value || !/^[a-z0-9*]+$/i.test(value) ? 'Must be alphanumeric or asterisk' : undefined;
export const alphaNumericUnderscore = value => !value || !/^[\w]+$/i.test(value) ? 'Must be alphanumeric' : undefined;
export const fourDigitsNumeric = value => !value || !/^[0-9]{4}$/i.test(value) ? 'Must be a four digit number' : undefined;
export const atLeastOneNumber = value => !value || !/[0-9]+/.test(value) ? 'Must have at least one number' : undefined;
export const atLeastOneLowercase = value => !value || !/[a-z]/.test(value) ? 'Must have at least one lowercase character' : undefined;
export const atLeastOneUppercase = value => !value || !/[A-Z]/.test(value) ? 'Must have at least one uppercase character' : undefined;
export const lettersOrNumbers = value => !value || !/[a-z0-9]+/i.test(value) ? 'Must have at least one number' : undefined;
export const atLeastOneSpecialChar = value => !value || !/[~!@#$%^&*_\-+=`|(){}[\]:;"'<>,.?/]+/.test(value) ? 'Must have at least one special character' : undefined;
export const noSpecialCharSpace = value => !value || /[ ~!@#$%^&*_\-+=`|(){}[\]:;"'<>,.?/]+/.test(value) ? 'No special characters or spaces allowed' : undefined;
export const noCapitalSpecialCharSpace = value => !value || /[ A-Z~!@#$%^&*_\-+=`|(){}[\]:;"'<>,.?/]+/.test(value) ? 'No capital letters, special characters or spaces allowed' : undefined;
export const noZeroOrOneStart = value =>
	value && (value[0] === '0' || value[0] === '1') ? 'Phone number cannot start with digit 0 or 1' : undefined;
export const email = value => !value || !/^[_A-Za-z0-9-+']+(\.[_A-Za-z0-9-+']+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*((\.[A-Za-z]{2,}){1}$)/.test(value) ? 'Must specify a valid email' : undefined;
export const phone = value => !value || !/^[01]?[- .]?(\([2-9]\d{2}\)|[2-9]\d{2})[- .]?\d{3}[- .]?\d{4}$/.test(value) ? 'Must specify valid phone number' : undefined;
export const phoneWithVoid = value => !/^[01]?[- .]?(\([2-9]\d{2}\)|[2-9]\d{2})[- .]?\d{3}[- .]?\d{4}$|^$/.test(value) ? 'Specify valid phone number or leave it empty' : undefined;
export const creditcard = value => value && !/^(?:(4[0-9]{12}(?:[0-9]{3})?)|(5[1-5][0-9]{14})|(2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9]{2}|7(?:[01][0-9]|20))[0-9]{12})|(6(?:011|5[0-9]{2})[0-9]{12})|(3[47][0-9]{13})|(3(?:0[0-5]|[68][0-9])[0-9]{11})|((?:2131|1800|35[0-9]{3})[0-9]{11})|([0-9]{12,19}))$/.test(value) ? 'Please enter a valid credit card number' : undefined;
export const noChange = previous => value =>
	!value || value === previous ? `No change detected` : undefined;
export const maskedInput = value => !value || !/[~*]/.test(value) ? 'Must be masked' : undefined;

export const alphaNumericOrEmail = (value) => {
	let msg1 = alphaNumeric(value);
	let msg2 = email(value);
	if (msg1 === undefined || msg2 === undefined) {
		return undefined;
	}
	return "Must be alphanumeric or a valid email";
};

export const alphaNumericUnderscoreOrEmail = (value) => {
	let msg1 = alphaNumericUnderscore(value);
	let msg2 = email(value);
	if (msg1 === undefined || msg2 === undefined) {
		return undefined;
	}
	return "Must be alphanumeric or a valid email";
};

export const maskedOrEmail = (value) => {
	let msg1 = maskedInput(value);
	let msg2 = email(value);
	if (msg1 === undefined || msg2 === undefined) {
		return undefined;
	}
	return "Must be a valid email.";
};
export const noMiddleSpace = text => value => /[^\s]+[\s]+[^\s]+/.test(value) ? `Please provide a valid ${text}` : undefined;
export const address = value => value && !/^[A-Za-z0-9.#&\s]+$/.test(value) ? 'No special characters allowed' : undefined;

/* eslint-enable no-confusing-arrow */



// WEBPACK FOOTER //
// ./src/helpers/validation.js